/**
 * LearningSession Component
 *
 * Card-by-card learning interface with flip-to-reveal interaction.
 * Displays 100 unreviewed cards with 4-button rating (AGAIN/HARD/GOOD/EASY).
 * Shows progress counter, handles completion and error states.
 */

import React, { useState, useEffect } from 'react';
import { apiClient as manaApi } from '../api/client';
import { Flashcard, LearningQuality } from '../types';
import './LearningSession.css';

interface LearningSessionState {
  cards: Flashcard[];
  currentIndex: number;
  isFlipped: boolean;
  isLoading: boolean;
  error: string | null;
}

// Learning quality constants for SM-2 algorithm
const LEARNING_QUALITY = {
  AGAIN: 0,
  HARD: 1,
  GOOD: 2,
  EASY: 3,
} as const;

const LearningSession: React.FC = () => {
  const [state, setState] = useState<LearningSessionState>({
    cards: [],
    currentIndex: 0,
    isFlipped: false,
    isLoading: true,
    error: null,
  });

  // Fetch unreviewed cards on component mount
  useEffect(() => {
    const fetchCards = async () => {
      try {
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        const response = await manaApi.getUnreviewedCards(undefined, 100);
        setState(prev => ({
          ...prev,
          cards: response.cards,
          isLoading: false,
        }));
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'カードを読み込めませんでした';
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
      }
    };

    fetchCards();
  }, []);

  // Handle card click to flip
  const handleCardClick = () => {
    setState(prev => ({ ...prev, isFlipped: !prev.isFlipped }));
  };

  // Handle rating button click
  const handleRating = async (quality: LearningQuality) => {
    const currentCard = state.cards[state.currentIndex];
    if (!currentCard) return;

    try {
      // Submit rating to backend
      await manaApi.rateCard(currentCard.card_id, quality);

      // Move to next card or show completion
      const nextIndex = state.currentIndex + 1;
      if (nextIndex >= state.cards.length) {
        // Session complete - stay on completion screen
        setState(prev => ({ ...prev, isFlipped: false }));
      } else {
        // Move to next card
        setState(prev => ({
          ...prev,
          currentIndex: nextIndex,
          isFlipped: false,
        }));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '評価を送信できませんでした';
      setState(prev => ({
        ...prev,
        error: errorMessage,
      }));
    }
  };

  // Handle starting a new session
  const handleNewSession = () => {
    setState({
      cards: [],
      currentIndex: 0,
      isFlipped: false,
      isLoading: true,
      error: null,
    });
    // Refetch cards
    manaApi.getUnreviewedCards(undefined, 100)
      .then(response => {
        setState(prev => ({
          ...prev,
          cards: response.cards,
          isLoading: false,
        }));
      })
      .catch(err => {
        const errorMessage = err instanceof Error ? err.message : 'カードを読み込めませんでした';
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
      });
  };

  // Handle starting over (reload page)
  const handleStartOver = () => {
    window.location.reload();
  };

  // Loading state
  if (state.isLoading) {
    return (
      <div className="learning-session__container">
        <div className="learning-session__loading">
          <div className="learning-session__spinner"></div>
          <p>カードを読み込み中...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (state.error) {
    return (
      <div className="learning-session__container">
        <div className="learning-session__error">
          <h2>エラー</h2>
          <p>{state.error}</p>
          <button className="learning-session__btn-primary" onClick={handleStartOver}>
            やり直す
          </button>
        </div>
      </div>
    );
  }

  // No cards state
  if (state.cards.length === 0) {
    return (
      <div className="learning-session__container">
        <div className="learning-session__empty">
          <h2>カードがありません</h2>
          <p>現在復習できるカードはありません。</p>
          <button className="learning-session__btn-primary" onClick={handleStartOver}>
            やり直す
          </button>
        </div>
      </div>
    );
  }

  // Completion state
  if (state.currentIndex >= state.cards.length) {
    return (
      <div className="learning-session__container">
        <div className="learning-session__complete">
          <h2>セッション完了！ 🎉</h2>
          <p>{state.cards.length}枚のカードすべてを評価しました。</p>
          <button className="learning-session__btn-primary" onClick={handleNewSession}>
            新しいセッションを開始
          </button>
        </div>
      </div>
    );
  }

  // Active learning state
  const currentCard = state.cards[state.currentIndex];
  const progress = `${state.currentIndex + 1} / ${state.cards.length}`;

  return (
    <div className="learning-session__container">
      <div className="learning-session__header">
        <h2>学習セッション</h2>
        <div className="learning-session__progress">カード {progress}</div>
      </div>

      <div className="learning-session__content">
        <div
          className={`flashcard ${state.isFlipped ? 'flipped' : ''}`}
          onClick={handleCardClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleCardClick();
            }
          }}
          aria-label="フラッシュカード - クリックで裏返す"
        >
          <div className="flashcard__front">
            <div className="flashcard__content">
              {currentCard.front_text}
            </div>
          </div>
          <div className="flashcard__back" style={{ display: state.isFlipped ? 'block' : 'none' }}>
            <div className="flashcard__content">
              <div className="flashcard__back-text">{currentCard.back_text}</div>
              {currentCard.reading && <div className="flashcard-reading">{currentCard.reading}</div>}
              {currentCard.meaning && <div className="flashcard-meaning">{currentCard.meaning}</div>}
              {currentCard.examples && currentCard.examples.length > 0 && (
                <div className="flashcard-examples">
                  {currentCard.examples.map((example, idx) => (
                    <div key={`${idx}-${example}`} className="example-item">{example}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {state.isFlipped && (
          <div className="learning-session__rating-buttons">
            <button
              className="learning-session__btn-rating learning-session__btn-rating--again"
              onClick={() => handleRating(LEARNING_QUALITY.AGAIN)}
              aria-label="カードを「もう一度」と評価"
            >
              もう一度
            </button>
            <button
              className="learning-session__btn-rating learning-session__btn-rating--hard"
              onClick={() => handleRating(LEARNING_QUALITY.HARD)}
              aria-label="カードを「難しい」と評価"
            >
              難しい
            </button>
            <button
              className="learning-session__btn-rating learning-session__btn-rating--good"
              onClick={() => handleRating(LEARNING_QUALITY.GOOD)}
              aria-label="カードを「良い」と評価"
            >
              良い
            </button>
            <button
              className="learning-session__btn-rating learning-session__btn-rating--easy"
              onClick={() => handleRating(LEARNING_QUALITY.EASY)}
              aria-label="カードを「簡単」と評価"
            >
              簡単
            </button>
          </div>
        )}

        {!state.isFlipped && (
          <p className="learning-session__flip-hint">カードをクリックして答えを表示</p>
        )}
      </div>
    </div>
  );
};

export default LearningSession;